import { Link } from '@tanstack/react-router';
import { AlertTriangle, ArrowLeft } from 'lucide-react';
import { Eyebrow } from '@/shared/components';
import type { PropertyGroupResponse } from '@/shared/api';
import { cn } from '@/shared/lib/cn';
import { ObjectActionsMenu } from './ObjectActionsMenu';
import type { PublicationBadge } from './objectPublicationBadge';

const BADGE_DOT: Record<PublicationBadge['tone'], string> = {
  ok: 'bg-sage',
  progress: 'bg-amber',
  error: 'bg-sealwax',
  neutral: 'ring-ink-faint ring-[1.5px] ring-inset',
};

/**
 * Wo bin ich, in welchem Zustand ist das Haus, was kann ich hier selten tun (§4). Auf dem Telefon
 * steht die Adresse auf eigener Zeile und der Stand in Kurzform, damit nichts hängend umbricht.
 */
export function ObjectHeader({
  group,
  canEdit,
  badge,
}: {
  group: PropertyGroupResponse;
  canEdit: boolean;
  /** Fehlt, solange der Serverstand noch unterwegs ist — kein Zwischenstand, der gleich kippt. */
  badge: PublicationBadge | null;
}) {
  const count = group.apartments.length;

  return (
    <header className="mb-6">
      <Link
        to="/property-groups"
        className="text-ink-soft hover:text-ink inline-flex min-h-11 items-center gap-1.5 text-[13px] font-medium transition-colors"
      >
        <ArrowLeft size={14} strokeWidth={2} aria-hidden />
        Alle Objekte
      </Link>

      <div className="mt-1 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <Eyebrow>Objekt</Eyebrow>
          <h1 className="text-ink mt-3 break-words font-serif text-3xl font-normal tracking-tight lg:text-4xl">
            {group.name}
            <span className="text-sealwax">.</span>
          </h1>
        </div>
        {canEdit && <ObjectActionsMenu group={group} />}
      </div>

      <p className="text-ink-soft mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
        <span className="basis-full sm:basis-auto">
          {group.address.street}, {group.address.postalCode} {group.address.city}
        </span>
        <Separator className="hidden sm:inline-block" />
        <span>
          {count} {count === 1 ? 'Apartment' : 'Apartments'}
        </span>
        {badge && (
          <>
            <Separator />
            <span
              className={cn(
                'inline-flex items-center gap-1.5 font-medium',
                badge.tone === 'ok' ? 'text-sage' : badge.tone === 'error' ? 'text-sealwax' : '',
              )}
            >
              <span className={cn('size-2 rounded-full', BADGE_DOT[badge.tone])} aria-hidden />
              <span className="sm:hidden">{badge.short}</span>
              <span className="hidden sm:inline">{badge.label}</span>
            </span>
          </>
        )}
      </p>

      {/* Während die Suche noch läuft, ist nichts zu tun — erst danach fragt der Kopf nach. */}
      {group.needsAddressReview && !group.positionLookupPending && (
        // Derselbe Warnton wie in der Navigation und auf der Adresskarte — ein Zustand, eine Farbe.
        <p
          className="border-amber/40 bg-amber/10 text-ink mt-4 flex items-start gap-2 rounded-md border p-4 text-sm"
          role="status"
        >
          <AlertTriangle size={16} className="text-amber mt-px shrink-0" aria-hidden />
          Adresse ungeprüft — bitte unter Stammdaten › Lage den Pin setzen oder bestätigen.
        </p>
      )}
    </header>
  );
}

function Separator({ className }: { className?: string }) {
  return <span className={cn('bg-ink-faint size-1 rounded-full', className)} aria-hidden />;
}
